import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function ManageDomains() {
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/allowed-domains`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDomains(response.data.domains || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
      setError('Failed to load domains');
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate domain format
    let domainToAdd = newDomain.trim().toLowerCase();
    
    if (!domainToAdd.startsWith('@')) {
      domainToAdd = '@' + domainToAdd;
    }

    if (domainToAdd.length < 3) {
      setError('Please enter a valid domain');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/admin/allowed-domains`,
        { domain: domainToAdd },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setDomains(response.data.domains);
      setNewDomain('');
      setSuccess(`Domain ${domainToAdd} added successfully!`);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to add domain');
    }
  };

  const handleRemoveDomain = async (domain) => {
    if (!window.confirm(`Are you sure you want to remove ${domain}?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(
        `${API}/api/admin/allowed-domains`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          data: { domain }
        }
      );
      
      setDomains(response.data.domains);
      setSuccess(`Domain ${domain} removed successfully!`);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to remove domain');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/mentality-logo.jpg" alt="Logo" className="h-10 w-10 rounded-full" />
              <div>
                <h1 className="text-xl font-bold text-[#5BB9B8]">Manage Domains</h1>
                <p className="text-xs text-gray-500">Configure allowed email domains</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 text-gray-700 hover:text-[#5BB9B8] transition-colors"
              >
                ← Back to Admin
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">About Allowed Domains</h3>
              <p className="mt-1 text-sm text-blue-700">
                Only students with email addresses from these domains can register for the platform. 
                Make sure to include the @ symbol (e.g., @gram.edu).
              </p>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        {/* Add Domain Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Add New Domain</h2>
          <form onSubmit={handleAddDomain} className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="Enter domain (e.g., @gram.edu or gram.edu)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5BB9B8]"
                required
              />
            </div>
            <button
              type="submit"
              className="px-6 py-2 bg-[#5BB9B8] text-white rounded-lg hover:bg-[#4a9998] transition-colors font-medium"
            >
              Add Domain
            </button>
          </form>
        </div>

        {/* Current Domains List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Current Allowed Domains ({domains.length})
          </h2>
          
          {domains.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No domains configured</p>
          ) : (
            <div className="space-y-3">
              {domains.map((domain, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#5BB9B8] transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#5BB9B8] rounded-full flex items-center justify-center text-white font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-mono text-lg text-gray-800">{domain}</p>
                      <p className="text-sm text-gray-500">
                        Students with {domain} emails can register
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveDomain(domain)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                    disabled={domains.length === 1}
                    title={domains.length === 1 ? "Cannot remove the last domain" : "Remove domain"}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Warning */}
        {domains.length === 1 && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-sm text-yellow-800">
                You have only one domain configured. At least one domain must remain for students to register.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageDomains;
